<template>
    <div
        class="redesign-table-header w-full"
        data-testid="table-header-container"
        :style="cssVars"
    >
        <Dropdown
            trigger="click"
            class="w-full"
            data-testid="table-header-dropdown"
            @visible-change="logVisibleChange"
        >
            <redesign-tooltip :class="'w-full'">
                <template #default>
                    <div
                        class="flex gap-1 items-center text-content"
                        data-testid="table-header-content"
                        @click.stop="handleClick"
                    >
                        <RedesignBaseIcon
                            v-if="showIcon"
                            class="table-header-icon"
                            data-testid="table-header-icon"
                            :name="column.label.iconLabel"
                        />
                        <div
                            class="table-header-label"
                            data-testid="table-header-label"
                        >
                            {{ computedLabel }}
                        </div>
                        <!-- Filter indicator dot -->
                        <div
                            v-if="hasActiveFilter"
                            class="filter-indicator-dot"
                        />
                    </div>
                </template>
                <template #tooltip-content>
                    <div>
                        {{ column.tooltipLabel }}
                    </div>
                </template>
            </redesign-tooltip>
            <template #dropdown>
                <DropdownMenu v-if="withManageColumns">
                    <div :class="{'border-b-2': radioInput !== 'iconOnly' && iconAdded}">
                        <redesign-radio-group
                            v-model="radioInput"
                            class="!flex flex-col gap-4 m-2 min-w-[212px]"
                            @change="handleRadioChange"
                        >
                            <redesign-radio
                                v-for="item in tableHeaderColumnOptionsComputed"
                                :key="item.label + column.prop"
                                :label="item.label"
                                class="dropdown-item"
                            >
                                {{ item.visibleLabel }}
                            </redesign-radio>
                        </redesign-radio-group>
                    </div>
                    <div
                        v-show="radioInput === tableHeaderColumnOptions.custom.label"
                        class="mx-3 mt-2 flex items-center gap-2"
                    >
                        <RedesignBaseInput
                            :value="customInputValue"
                            class="flex-1 min-w-0 max-w-[150px]"
                            @input="handleCustomInputChange"
                            @update:modelValue="handleCustomInputChange"
                            @keyup.enter="applyCustomLabel"
                            @keyup.esc="cancelCustomLabel"
                        />
                        <div
                            v-show="hasUnsavedCustomChanges"
                            class="flex items-center gap-1 flex-shrink-0"
                        >
                            <RedesignBaseIconButton
                                icon="Check"
                                :size="16"
                                color="primary"
                                @click.stop="applyCustomLabel"
                            />
                            <RedesignBaseIconButton
                                icon="X"
                                :size="16"
                                color="secondary"
                                @click.stop="cancelCustomLabel"
                            />
                        </div>
                    </div>
                    <div
                        v-show="radioInput !== tableHeaderColumnOptions.iconOnly.label && iconAdded"
                        class="mx-3 mt-2"
                    >
                        <redesign-checkbox
                            size="medium"
                            label="showIcon"
                            :checked="iconCheckboxValue"
                            @update:checked="handleIconCheckboxChange"
                        >
                            {{ i18n.t('widget.table.headerShowIconOption') }}
                        </redesign-checkbox>
                    </div>
                    <div class="mx-3 mt-4 mb-2 border-t pt-3">
                        <WidgetTableColumnsAlignment
                            :alignment="columnAlignment"
                            @update:alignment="handleAlignmentChange"
                        />
                    </div>
                    <!-- Filter section -->
                    <div
                        v-if="column.filterableConfig"
                        class="mx-3 mt-4 mb-2 border-t pt-3"
                    >
                        <component
                            :is="column.filterableConfig?.component || RedesignColumnFilterOptions"
                            :selected-values="currentFilterValues ?? []"
                            v-bind="column.filterableConfig?.componentProps || {}"
                            @filter-change="handleFilterChange"
                        />
                    </div>
                </DropdownMenu>
            </template>
        </Dropdown>
    </div>
</template>

<script lang="ts" setup>
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignBaseIconButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseIconButton.vue'
import { Dropdown, DropdownMenu } from 'element-ui'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadio from '@/components/RedesignBaseComponents/Inputs/RedesignRadio.vue'
import RedesignCheckbox from '@/components/RedesignBaseComponents/Inputs/RedesignCheckbox.vue'
import WidgetTableColumnsAlignment from '@/components/RedesignComponents/Widgets/Common/WidgetTableColumnsAlignment.vue'
import RedesignColumnFilterOptions from '@/components/RedesignComponents/RedesignTableComponents/Filters/RedesignColumnFilterOptions.vue'
import { computed, ref, watch } from 'vue'
import { ColumnConfig, TableHeadersConfig, WidgetTemplateColumnWithConfig, FilterConfig } from '@/types/dashboard'
import { getTableHeaderColumnOptions, getTableHeaderGlobalOptions } from '@/enum/redesignTableHeaderOptions'
import i18n from '@/i18n'
import type { TableAlignmentType } from '@/types/alignment'

// Emits
const emit = defineEmits<{
    (event: 'update-column-config', column: string, payload: ColumnConfig): void
    (event: 'update-global-headers-config', payload: TableHeadersConfig): void
    (event: 'filter-change', column: string, values: Array<string | number>): void
}>()

// Props
const props = defineProps<{
    column: WidgetTemplateColumnWithConfig & {
        filterableConfig?: FilterConfig
    }
    columnConfig: ColumnConfig
    globalHeadersConfig: TableHeadersConfig,
    fontSize?: string
    withManageColumns: boolean
    currentFilterValues?: Array<string | number>
}>()

// State
const radioInput = ref('')
const iconCheckboxValue = ref(false)
const localConfig = ref<ColumnConfig>(props.columnConfig)
const isDropdownChange = ref(false)
const customInputValue = ref('')

// Computed
// Get table header options with translated labels at runtime
const tableHeaderGlobalOptions = computed(() => getTableHeaderGlobalOptions())
const tableHeaderColumnOptions = computed(() => getTableHeaderColumnOptions())

const cssVars = computed(() => {
    return {
        '--dynamic-header-font-size': props.fontSize
    }
})

const isCustomColumn = computed(() => {
    return radioInput.value === tableHeaderColumnOptions.value.custom.label
})

const isGlobalCustomMode = computed(() => {
    return props.globalHeadersConfig.labelTextType === tableHeaderGlobalOptions.value.custom.label
})

const hasActiveCustomLabel = computed(() => {
    const hasColumnLabelOverride = localConfig.value.showShortTextLabel || localConfig.value.showFullTextLabel
    return !hasColumnLabelOverride &&
        (isCustomColumn.value || isGlobalCustomMode.value) &&
        localConfig.value.customTextLabel != null &&
        localConfig.value.customTextLabel !== ''
})

const computedLabel = computed(() => {
    if (showFullLabel.value) return props.column.label?.fullTextLabel || ''
    if (showShortLabel.value) return props.column.label?.shortTextLabel || ''
    if (isCustomColumn.value && (localConfig.value.customTextLabel || customInputValue.value)) {
        return localConfig.value.customTextLabel || customInputValue.value
    }
    if (isGlobalCustomMode.value && localConfig.value.customTextLabel) return localConfig.value.customTextLabel
    return props.column.label?.fullTextLabel || props.column.label?.shortTextLabel || ''
})

const showIcon = computed(() => {
    if (!localConfig.value) {
        return false
    }
    return localConfig.value.showIcon && iconAdded.value
        || iconCheckboxValue.value && iconAdded.value
        || props.globalHeadersConfig.showAllIcons && iconAdded.value
})

const showShortLabel = computed(() => {
    if (!localConfig.value) return false
    if (hasActiveCustomLabel.value) return false
    if (localConfig.value.showShortTextLabel) return true
    if (localConfig.value.showFullTextLabel) return false
    return props.globalHeadersConfig.labelTextType === tableHeaderGlobalOptions.value.shortText.label
})

const iconAdded = computed(() => {
    if (props.column.label) {
        return !!props.column.label.iconLabel
    }
    return false

})

const tableHeaderColumnOptionsComputed = computed(() => {
    const base = {
        shortText: tableHeaderColumnOptions.value.shortText,
        fullText: tableHeaderColumnOptions.value.fullText,
        custom: tableHeaderColumnOptions.value.custom,
    }
    return iconAdded.value
        ? {
            iconOnly: tableHeaderColumnOptions.value.iconOnly,
            ...base
        }
        : base
})

const showFullLabel = computed(() => {
    if (!localConfig.value) return false
    if (hasActiveCustomLabel.value) return false
    if (localConfig.value.showFullTextLabel) return true
    if (localConfig.value.showShortTextLabel) return false
    return props.globalHeadersConfig.labelTextType === tableHeaderGlobalOptions.value.fullText.label
})

const columnAlignment = computed(() => {
    return localConfig.value?.align
})

// Check if filter is active (values differ from default - not all selected)
const hasActiveFilter = computed(() => {
    if (!props.column.filterableConfig) return false

    const selectedCount = (props.currentFilterValues ?? []).length
    if (selectedCount === 0) return false

    const filterOptions = props.column.filterableConfig.componentProps?.filterOptions
    if (!Array.isArray(filterOptions) || filterOptions.length === 0) {
        return selectedCount > 0
    }

    return selectedCount < filterOptions.length
})

const savedCustomLabel = computed(() => {
    return localConfig.value.customTextLabel ?? props.column.label?.fullTextLabel ?? ''
})

const hasUnsavedCustomChanges = computed(() => {
    return radioInput.value === tableHeaderColumnOptions.value.custom.label &&
        customInputValue.value !== savedCustomLabel.value
})

// Watchers
watch(
    () => props.globalHeadersConfig,
    (val) => {
        if (val && val.labelTextType === tableHeaderGlobalOptions.value.custom.label) {
            if (isDropdownChange.value) {
                localConfig.value = {
                    ...props.columnConfig,
                    ...localConfig.value
                }
            } else {
                localConfig.value = {
                    ...props.columnConfig
                }
            }
            radioInput.value = getRadioInputFromConfig(localConfig.value, tableHeaderColumnOptions.value.fullText.label)
            customInputValue.value = localConfig.value.customTextLabel || props.column.label?.fullTextLabel || ''
        } else {
            localConfig.value = { ...props.columnConfig }
            radioInput.value = getRadioInputFromConfig(localConfig.value, val.labelTextType)
            customInputValue.value = localConfig.value.customTextLabel || props.column.label?.fullTextLabel || ''
            isDropdownChange.value = false
        }
    },
    { immediate: true }
)

// Methods
const handleClick = (event: Event) => {
    event.stopPropagation()
}

const handleIconCheckboxChange = (checked?: string | number | boolean) => {
    iconCheckboxValue.value = !!checked
}

const handleAlignmentChange = (alignment: TableAlignmentType) => {
    localConfig.value = {
        ...localConfig.value,
        align: alignment
    }
    emit('update-column-config', props.column.prop, localConfig.value)
}

const handleRadioChange = (value: string) => {
    isDropdownChange.value = true
    if (value === tableHeaderColumnOptions.value.custom.label) {
        if (props.globalHeadersConfig.labelTextType !== tableHeaderGlobalOptions.value.custom.label) {
            emit('update-global-headers-config', {
                labelTextType: tableHeaderGlobalOptions.value.custom.label,
                showAllIcons: false,
            })
        }
        const initialCustomLabel = localConfig.value.customTextLabel ?? props.column.label?.fullTextLabel ?? ''
        customInputValue.value = initialCustomLabel
        localConfig.value = {
            ...localConfig.value,
            showIcon: value === tableHeaderColumnOptions.value.iconOnly.label || iconCheckboxValue.value,
            showShortTextLabel: false,
            showFullTextLabel: false,
            customTextLabel: initialCustomLabel,
        }
    } else {
        localConfig.value = {
            ...localConfig.value,
            showIcon: value === tableHeaderColumnOptions.value.iconOnly.label || iconCheckboxValue.value,
            showShortTextLabel: value === tableHeaderColumnOptions.value.shortText.label,
            showFullTextLabel: value === tableHeaderColumnOptions.value.fullText.label,
        }
    }
    emit('update-column-config', props.column.prop, localConfig.value)
}

const handleFilterChange = (values: Array<string | number>) => {
    emit('filter-change', props.column.prop, values)
}


function applyCustomLabel () {
    const trimmed = customInputValue.value.trim()
    customInputValue.value = trimmed
    localConfig.value = {
        ...localConfig.value,
        customTextLabel: trimmed || undefined,
    }
    if (trimmed) {
        emit('update-column-config', props.column.prop, localConfig.value)
    } else {
        const { customTextLabel, ...rest } = localConfig.value
        localConfig.value = rest as ColumnConfig
        emit('update-column-config', props.column.prop, localConfig.value)
    }
    if (props.globalHeadersConfig.labelTextType !== tableHeaderGlobalOptions.value.custom.label) {
        emit('update-global-headers-config', {
            labelTextType: tableHeaderGlobalOptions.value.custom.label,
            showAllIcons: false,
        })
    }
}

function cancelCustomLabel () {
    customInputValue.value = localConfig.value.customTextLabel || props.column.label?.fullTextLabel || ''
}

function handleCustomInputChange (val: string | number | undefined) {
    customInputValue.value = val !== undefined && val !== null ? String(val) : ''
}
function getRadioInputFromConfig (config: ColumnConfig, globalLabelType: string) {
    const opts = tableHeaderColumnOptions.value
    if (iconAdded.value && config?.showIcon) return opts.iconOnly.label
    if (config?.showShortTextLabel) return opts.shortText.label
    if (config?.showFullTextLabel) return opts.fullText.label
    if (config?.customTextLabel != null && config.customTextLabel !== '') return opts.custom.label
    return globalLabelType || opts.fullText.label
}

const logVisibleChange = (isVisible: boolean) => {
    if (isVisible) {
        localConfig.value = { ...props.columnConfig }
        radioInput.value = getRadioInputFromConfig(localConfig.value, props.globalHeadersConfig.labelTextType)
        customInputValue.value = localConfig.value.customTextLabel || props.column.label?.fullTextLabel || ''
    }
    if (!isVisible) {
        emit('update-column-config', props.column.prop, localConfig.value)
    }
}

watch(() => props.columnConfig, (newVal) => {
    if (newVal) {
        localConfig.value = { ...newVal }
        radioInput.value = getRadioInputFromConfig(localConfig.value, props.globalHeadersConfig?.labelTextType ?? '')
        customInputValue.value = localConfig.value.customTextLabel || props.column.label?.fullTextLabel || ''
    }
}, { deep: true })

</script>

<style lang="scss">
.redesign-table-header {
    .table-header-icon {
        @apply text-default-text;
        &:hover {
            @apply text-primary;
        }
    }

    .dropdown-item {
        @apply text-primary;
    }
    .table-header-label {
        max-width: 100%;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        color: var(--colorTableHeaderText-color, #606266);
        font-size: var(--dynamic-header-font-size);
        line-height: calc(var(--dynamic-header-font-size) * 1.2);
        &:hover {
            color: color-mix(
                    in srgb,
                    var(--colorTableHeaderText-color, var(--primary-color)) 70%,
                    white)
        }
    }
    .filter-indicator-dot {
        position: absolute;
        top: 0px;
        left: -4px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--accent-color);
    }
}
</style>
